import {
  htmlToFieldText as htmlToFieldTextRef,
  domPositionToFieldOffset,
  fieldOffsetToDomPosition,
  isMultilineField,
} from '@/lib/resume/inline-edit';

/**
 * Wire click-to-edit into one rendered preview page.
 *
 * The preview is a same-origin `srcDoc` iframe, so listeners go *inside* its
 * document rather than on the host page. That matters: the page is rendered
 * under a `transform: scale()` and a `translateY()` offset, and coordinates
 * from an inside-the-document event are already in that document's own space,
 * so caret hit-testing needs no transform maths to undo.
 */

export interface InlineEditTarget {
  /** Stamped field path, e.g. `employment.<id>.responsibilities`. */
  path: string;
  multiline: boolean;
  /**
   * A `string[]` rendered as chips (skills).
   *
   * These must never be edited in place: the chips are inline spans, so reading
   * the edited markup back would weld every skill onto one line and collapse
   * the list to a single entry. The caller opens the text sheet instead.
   */
  list: boolean;
  /** The stamped element, so the caller can edit it in place. */
  element: HTMLElement;
  /** Caret offset within the field's text. */
  caretOffset: number;
}

/**
 * A click on a rendered date range.
 *
 * Dates are never edited as text — `Jan 2025 - Present` is rendered from
 * `startDate`/`endDate`/`isCurrentlyWorking`, and parsing that back out of free
 * text is a reliable way to corrupt the record. The caller opens a picker.
 */
export interface InlineDateTarget {
  /** Owning record, e.g. `employment.<id>`. */
  path: string;
  element: HTMLElement;
}

const HOVER_STYLE_ID = 'resume-inline-edit-style';

const HOVER_CSS = `
  [data-rf] { cursor: text; }
  [data-rf-date] { cursor: pointer; }
  [data-rf-date]:hover {
    outline: 1px dashed var(--resume-color-accent, #7f1d1d);
    outline-offset: 3px;
    border-radius: 2px;
  }
  [data-rf]:hover {
    outline: 1px dashed var(--resume-color-accent, #7f1d1d);
    outline-offset: 3px;
    border-radius: 2px;
  }
  [data-rf][data-rf-editing="true"] {
    outline: 2px solid var(--resume-color-accent, #7f1d1d);
    outline-offset: 3px;
    background: rgba(127, 29, 29, 0.04);
  }
`;

function caretRangeFrom(doc: Document, x: number, y: number): Range | null {
  // caretRangeFromPoint is Blink/WebKit; caretPositionFromPoint is the standard
  // Firefox implements. Neither is universal, so try both before giving up.
  const withRange = doc as Document & { caretRangeFromPoint?: (x: number, y: number) => Range | null };
  if (typeof withRange.caretRangeFromPoint === 'function') {
    return withRange.caretRangeFromPoint(x, y);
  }

  const withPosition = doc as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  if (typeof withPosition.caretPositionFromPoint === 'function') {
    const position = withPosition.caretPositionFromPoint(x, y);
    if (!position) return null;
    const range = doc.createRange();
    range.setStart(position.offsetNode, position.offset);
    range.collapse(true);
    return range;
  }

  return null;
}

/**
 * Offset of the caret within the field's stored text.
 *
 * Clones everything from the start of the field up to the caret and measures it
 * with the SAME normalizer that produces the field's text, so the number is an
 * index into exactly the string the editor receives.
 *
 * The previous implementation walked the DOM and counted raw `nodeValue`
 * lengths, adding two characters per `<li>` for its `- ` marker. That agreed
 * with the stored text only for minified markup — which is all the tests used.
 * The real templates are pretty-printed, so every `<ul>`/`<li>` boundary carries
 * newline-and-indent text nodes that exist in the DOM but not in the stored
 * text. Counting them pushed the caret further with every bullet crossed, which
 * is why single-line fields behaved and lists drifted by several characters.
 */
export function caretOffsetInField(element: HTMLElement, caretNode: Node, caretOffset: number): number {
  if (!element.contains(caretNode)) return 0;
  return domPositionToFieldOffset(element, caretNode, caretOffset);
}

export interface InlineEditHandlers {
  onRequestEdit: (target: InlineEditTarget) => void;
  onRequestDateEdit?: (target: InlineDateTarget) => void;
}

/**
 * Attach hover affordances and click-to-edit to a preview document.
 * Returns a cleanup that removes everything it added.
 */
export function attachInlinePreviewEditing(
  doc: Document,
  { onRequestEdit, onRequestDateEdit }: InlineEditHandlers
): () => void {
  if (!doc.body) return () => undefined;

  if (!doc.getElementById(HOVER_STYLE_ID)) {
    const style = doc.createElement('style');
    style.id = HOVER_STYLE_ID;
    style.textContent = HOVER_CSS;
    doc.head?.appendChild(style);
  }

  const handleClick = (event: MouseEvent) => {
    const target = event.target as Element | null;

    // Dates are checked first: a date span can sit inside an entry that also
    // carries text fields, and a caret must never land in a rendered range.
    const dateElement = target?.closest?.('[data-rf-date]') as HTMLElement | null;
    const datePath = dateElement?.getAttribute('data-rf-date');
    if (dateElement && datePath) {
      event.preventDefault();
      event.stopPropagation();
      onRequestDateEdit?.({ path: datePath, element: dateElement });
      return;
    }

    const element = target?.closest?.('[data-rf]') as HTMLElement | null;
    const path = element?.getAttribute('data-rf');
    if (!element || !path) return;

    // Already editing THIS field: let the browser move the caret normally.
    // Restarting the session would re-run the scroll and yank the view back to
    // where it started, so clicking a second word in the same paragraph felt
    // like the page jumped away from you.
    if (element.getAttribute('data-rf-editing') === 'true') return;

    event.preventDefault();
    event.stopPropagation();

    const range = caretRangeFrom(doc, event.clientX, event.clientY);
    const caretOffset =
      range && element.contains(range.startContainer)
        ? caretOffsetInField(element, range.startContainer, range.startOffset)
        : 0;

    const list = element.getAttribute('data-rf-list') === 'true';
    onRequestEdit({
      path,
      multiline: isMultilineField(path) || list,
      list,
      element,
      caretOffset: list ? 0 : caretOffset,
    });
  };

  doc.addEventListener('click', handleClick, true);

  return () => {
    doc.removeEventListener('click', handleClick, true);
    doc.getElementById(HOVER_STYLE_ID)?.remove();
  };
}

export interface InPlaceEditHandlers {
  onCommit: (path: string, value: string) => void;
  onCancel?: () => void;
}

/**
 * Turn the clicked block into a live editable region, caret where they clicked.
 *
 * Editing the real element rather than an overlay input means the text keeps
 * the template's own font, size, leading and wrapping for free — an overlay
 * would have to re-derive all of it and would drift the moment a line wrapped.
 *
 * Commits on blur and on Cmd/Ctrl+Enter; Escape restores the original markup.
 * Single-line fields also commit on plain Enter, since a newline is meaningless
 * in a job title.
 */
export interface InPlaceEditSession {
  /** Save and exit. Backs the explicit Save control on touch. */
  commit: () => void;
  /** Discard and restore the original markup. */
  cancel: () => void;
}

export function beginInPlaceEdit(
  target: InlineEditTarget,
  { onCommit, onCancel }: InPlaceEditHandlers
): InPlaceEditSession {
  const { element, path, multiline, caretOffset } = target;
  const doc = element.ownerDocument;
  const originalHtml = element.innerHTML;
  let settled = false;

  element.setAttribute('contenteditable', 'plaintext-only');
  element.setAttribute('data-rf-editing', 'true');
  element.setAttribute('spellcheck', 'true');

  const cleanup = () => {
    element.removeAttribute('contenteditable');
    element.removeAttribute('data-rf-editing');
    element.removeEventListener('keydown', handleKeyDown);
    element.removeEventListener('blur', handleBlur);
  };

  const commit = () => {
    if (settled) return;
    settled = true;
    // Read the text back through the same conversion the field expects, so a
    // rendered <ul> returns as "- " markered lines rather than welded prose.
    const value = htmlToFieldTextRef(element);
    cleanup();
    onCommit(path, value);
  };

  const cancel = () => {
    if (settled) return;
    settled = true;
    element.innerHTML = originalHtml;
    cleanup();
    onCancel?.();
  };

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
      return;
    }
    if (event.key === 'Enter' && (!multiline || event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      commit();
    }
  }

  function handleBlur() {
    commit();
  }

  element.addEventListener('keydown', handleKeyDown);
  element.addEventListener('blur', handleBlur);

  element.focus({ preventScroll: true });
  placeCaret(doc, element, caretOffset);

  return { commit, cancel };
}

/** Put the caret `offset` characters into the field's text. */
export function placeCaret(doc: Document, element: HTMLElement, offset: number): void {
  const selection = doc.defaultView?.getSelection();
  if (!selection) return;

  // Resolved through the same walk that produced the offset. The previous
  // version counted raw DOM text, so every "- " marker and every scrap of
  // template indentation shifted the result — and once the markers pushed the
  // offset past the raw length it silently parked the caret at the very end.
  const position = fieldOffsetToDomPosition(element, offset);

  const range = doc.createRange();
  if (position) {
    range.setStart(position.node, Math.min(position.offset, (position.node.nodeValue ?? '').length));
    range.collapse(true);
  } else {
    range.selectNodeContents(element);
    range.collapse(false);
  }

  selection.removeAllRanges();
  selection.addRange(range);
}
